/**
 * Centralized measurement-unit conversion helpers.
 *
 * Canonical storage is ALWAYS metric (temperature in °C, weight in kg).
 * Conversions happen exclusively at the UI/input/output boundary — statistics,
 * insight engines and the database never see imperial values.
 */

export type UnitSystem = "metric" | "us";

export const KG_PER_LB = 2.2046226218;

/* ---------------------------------- temperature --------------------------------- */

export function cToF(c: number): number {
  return c * 9 / 5 + 32;
}

export function fToC(f: number): number {
  return (f - 32) * 5 / 9;
}

/* ------------------------------------ weight ------------------------------------ */

export function kgToLb(kg: number): number {
  return kg * KG_PER_LB;
}

export function lbToKg(lb: number): number {
  return lb / KG_PER_LB;
}

/* ------------------------------------ labels ------------------------------------ */

export function tempUnit(system: UnitSystem): string {
  return system === "us" ? "°F" : "°C";
}

export function weightUnit(system: UnitSystem): string {
  return system === "us" ? "lb" : "kg";
}

/* ------------------------------ display conversions ----------------------------- */

/** Canonical °C -> value shown to the user (whole degrees). */
export function tempToDisplay(c: number | null | undefined, system: UnitSystem): number | null {
  if (c == null || Number.isNaN(c)) return null;
  return Math.round(system === "us" ? cToF(c) : c);
}

/** User-entered temperature -> canonical °C (whole degrees, matching input step). */
export function tempToCanonical(v: number | null | undefined, system: UnitSystem): number | null {
  if (v == null || Number.isNaN(v)) return null;
  return Math.round(system === "us" ? fToC(v) : v);
}

/** Canonical kg -> value shown to the user (1 decimal keeps round-trips stable). */
export function weightToDisplay(kg: number | null | undefined, system: UnitSystem): number | null {
  if (kg == null || Number.isNaN(kg)) return null;
  return Math.round((system === "us" ? kgToLb(kg) : kg) * 10) / 10;
}

/** User-entered weight -> canonical kg (2 decimals: sub-0.1 lb round-trip drift). */
export function weightToCanonical(v: number | null | undefined, system: UnitSystem): number | null {
  if (v == null || Number.isNaN(v)) return null;
  return Math.round((system === "us" ? lbToKg(v) : v) * 100) / 100;
}

/** Formatted temperature with unit suffix, e.g. "84°F". */
export function formatTemp(c: number | null | undefined, system: UnitSystem, decimals = 0): string {
  if (c == null || Number.isNaN(c)) return "—";
  const v = system === "us" ? cToF(c) : c;
  return `${v.toFixed(decimals)}${tempUnit(system)}`;
}

/** Formatted weight with unit suffix, e.g. "195.1 lb". */
export function formatWeight(kg: number | null | undefined, system: UnitSystem, decimals = 1): string {
  if (kg == null || Number.isNaN(kg)) return "—";
  const v = system === "us" ? kgToLb(kg) : kg;
  return `${v.toFixed(decimals)} ${weightUnit(system)}`;
}

/** Sensible numeric input bounds/step per unit system. */
export const inputRanges = {
  temp: (system: UnitSystem) =>
    system === "us" ? { min: 14, max: 122, step: 1, placeholder: "72" } : { min: -10, max: 50, step: 1, placeholder: "22" },
  weight: (system: UnitSystem) =>
    system === "us" ? { min: 45, max: 550, step: 0.1, placeholder: "165" } : { min: 20, max: 250, step: 0.1, placeholder: "75" },
};

/**
 * Locale-based default. Only en-US (and explicit US-territory locales) start on
 * US units; every other supported locale starts metric. No IP geolocation.
 */
export function defaultSystemForLocale(locales?: readonly string[]): UnitSystem {
  const list =
    locales ??
    (typeof navigator !== "undefined"
      ? (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language])
      : []);
  for (const raw of list) {
    if (!raw) continue;
    const tag = raw.toLowerCase();
    if (tag === "en-us" || tag.startsWith("en-us-")) return "us";
    // First usable tag decides; anything else is metric.
    return "metric";
  }
  return "metric";
}

/* ------------------------------------ height ------------------------------------ */

export const CM_PER_INCH = 2.54;

/** Canonical cm -> { ft, in } with the 12-inch rounding edge case handled. */
export function cmToFtIn(cm: number | null | undefined): { ft: number; in: number } | null {
  if (cm == null || Number.isNaN(cm)) return null;
  const totalInches = cm / CM_PER_INCH;
  let ft = Math.floor(totalInches / 12);
  let inches = Math.round(totalInches - ft * 12);
  if (inches === 12) {
    ft += 1;
    inches = 0;
  }
  return { ft, in: inches };
}

/** ft + in -> canonical cm (whole centimetres, matching the metric input step). */
export function ftInToCm(ft: number | null | undefined, inches: number | null | undefined): number | null {
  const f = ft == null || Number.isNaN(ft) ? 0 : ft;
  const i = inches == null || Number.isNaN(inches) ? 0 : inches;
  if ((ft == null || Number.isNaN(ft)) && (inches == null || Number.isNaN(inches))) return null;
  return Math.round((f * 12 + i) * CM_PER_INCH);
}

export function heightUnit(system: UnitSystem): string {
  return system === "us" ? "ft/in" : "cm";
}

/** Formatted height, e.g. "5 ft 3 in" or "160 cm". */
export function formatHeight(cm: number | null | undefined, system: UnitSystem): string {
  if (cm == null || Number.isNaN(cm)) return "—";
  if (system !== "us") return `${Math.round(cm)} cm`;
  const v = cmToFtIn(cm)!;
  return `${v.ft} ft ${v.in} in`;
}
